// ─────────────────────────────────────────────────────────────────────────────
// graphLod — semantic level, and what is resolved at each one.
//
// VISIBILITY IS SEMANTIC ZOOM, NOT DELETION. The store always holds the whole
// graph; this decides how much of it the renderer resolves from where the camera
// is standing. Zooming out must never mean losing data.
//
// LEVEL IS RELATIVE TO THE FOCUS, NOT TO CAMERA Z. Standing 300 units from a
// memory cluster is deep inside it; standing 300 units from City Hall is a
// city-wide view. Distance alone cannot tell those apart, so the level comes
// from cameraDistance / focusRadius.
//
// HYSTERESIS IS THE WHOLE POINT OF THE BAND TABLE. Without it a level boundary
// sits exactly where a scroll wheel rests, and the graph rebuilds its buffers
// every few milliseconds while the user does nothing.
// ─────────────────────────────────────────────────────────────────────────────

import { RexyGraphNodeKind } from './graphTypes';

export type SemanticLevel = 0 | 1 | 2 | 3 | 4;

/** Upper bound of normalised distance for each level, from the specification. */
const LEVEL_BANDS: Array<{ level: SemanticLevel; max: number }> = [
  { level: 4, max: 0.55 },
  { level: 3, max: 1.4 },
  { level: 2, max: 3.0 },
  { level: 1, max: 6.0 },
  { level: 0, max: Infinity },
];

/** Fraction of a band a boundary must be overshot by before the level flips. */
const HYSTERESIS = 0.12;

export function levelForDistance(normalisedDistance: number, current: SemanticLevel): SemanticLevel {
  let raw: SemanticLevel = 0;
  for (let i = 0; i < LEVEL_BANDS.length; i++) {
    if (normalisedDistance < LEVEL_BANDS[i].max) {
      raw = LEVEL_BANDS[i].level;
      break;
    }
  }
  if (raw === current) return current;

  // Moving between levels requires clearing the boundary by the hysteresis
  // margin, so a camera resting on a threshold stays where it is.
  const band = LEVEL_BANDS.find((b) => b.level === (raw > current ? current : raw));
  if (!band || band.max === Infinity) return raw;
  const margin = band.max * HYSTERESIS;
  if (raw > current && normalisedDistance < band.max + margin) return current;
  if (raw < current && normalisedDistance > band.max - margin) return current;
  return raw;
}

/**
 * SEMANTIC ZOOM DIMS; IT DOES NOT DELETE.
 *
 * The first cut of this hid deep kinds outright at low levels, and the far view
 * came back with forty-six points in it — a correct hierarchy and no galaxy. The
 * specification asks for "faint aggregate density for deeper structures" from
 * far away, and every leaf here is a REAL node, so the honest way to render deep
 * structure at a distance is faintly, not absently.
 *
 * So the level scales ALPHA by how deep a kind sits. From outside, the spine is
 * bright and the memory clouds are a haze; from inside a mansion the records
 * resolve to full strength. Nothing is ever dropped for being far away — which
 * is also why a search can still find a record the camera has never visited.
 */
type DepthRank = 0 | 1 | 2;

const DEPTH_RANK: { [kind: string]: DepthRank } = {
  'city-hall': 0, domain: 0, agent: 0,
  'city-hall-room': 1, 'memory-cluster': 1, 'memory-system': 1,
  tool: 1, database: 1, workflow: 1,
  model: 1, worker: 1, service: 1, surface: 1, evidence: 1,
  'memory-record': 2, 'memory-file': 2, file: 2, document: 2,
  thread: 2, artifact: 2, citation: 2, unknown: 2,
};

/**
 * [rank][level] → alpha multiplier. Rank 0 is the spine and never dims.
 *
 * STAGE C/D RAISED THE DEEP TIERS. At the old leaf value of 0.22, a LOW-trust
 * record at galaxy distance resolved to 0.55 × 0.52 × 0.22 ≈ 0.063 — mathematically
 * present and visually gone, which is why the far view read as a wireframe with
 * beads. The whole system is meant to contribute to the neural sphere from
 * outside; detail RESOLVES on approach rather than switching on.
 */
const LEVEL_ALPHA: number[][] = [
  [1, 1, 1, 1, 1],
  [0.62, 0.78, 1, 1, 1],
  [0.42, 0.55, 0.72, 1, 1],
];

export function levelAlphaFor(kind: RexyGraphNodeKind, level: SemanticLevel): number {
  const rank = DEPTH_RANK[kind];
  return LEVEL_ALPHA[rank === undefined ? 2 : rank][level];
}

/**
 * Camera distance that frames a node of a given kind — the destination of a
 * focus fly. Derived from the layout's own radius bands so a mansion is framed
 * at mansion scale and a record at reading scale.
 */
export function focusDistanceFor(kind: RexyGraphNodeKind): number {
  if (kind === 'city-hall') return 2400;
  if (kind === 'domain') return 520;
  if (kind === 'agent') return 190;
  if (kind === 'memory-cluster') return 70;
  if (kind === 'memory-system') return 70;
  return 26;
}

/**
 * The radius the focused node's own neighbourhood occupies. Normalised distance
 * is measured against this, which is what makes the level relative to what you
 * are looking at rather than to the origin.
 */
export function focusRadiusFor(kind: RexyGraphNodeKind): number {
  if (kind === 'city-hall') return 700;
  if (kind === 'domain') return 210;
  if (kind === 'agent') return 80;
  if (kind === 'memory-cluster') return 24;
  return 8;
}
